import { FieldArray, FieldArrayRenderProps, useField } from 'formik';
import { debounce } from 'lodash';
import React, { useCallback, useContext, useRef } from 'react';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { ReactComponent as DeleteIcon } from '../../../../../images/icons/delete.svg';
import { ButtonIcon, SecondaryButton } from '../../../../../shared/Button';
import { SelectField } from '../../../../../shared/form/inputs/SelectField';
import { TextInputField } from '../../../../../shared/form/inputs/TextInputField';
import { Header3 } from '../../../../../shared/Headers';
import { Table, TBody, Td, Th, THead, Tr } from '../../../../../shared/Table';
import { spacing04, spacing06 } from '../../../../../styling/spacing';
import { DesignFormDefaultsContext } from '../../../../designs/DesignFormDefaultsContext';
import { QuoteOrDesignFormContext } from '../../QuoteOrDesignFormContext';
import {
  DownlossTypeCode,
  downlossTypeCodeSelectOptions,
  getDownlossTypeDefaultTemperatures,
} from '../../rooms/heatLoss/downlossTypeCode';
import { getNewRoomFloorHeatLossDetailsFormModel } from '../../rooms/heatLoss/roomFloorHeatLossDetailsFormModel';
import { UnitFormModel } from '../unitFormModel';
import { onUnitFloorTemperatureChange } from './unitElementOnChangeHelpers';
import { getDefaultUnitFloorElementFormModel } from './unitFloorElementFormModel';
import { getUnitDefaultExternalAirTemperature } from './unitHeatLossDetailsFormModel';

type Props = {
  unit: UnitFormModel;
  unitIndex: number;
};

export const UnitFloorElementsField = ({ unit, unitIndex }: Props) => {
  const floorElementsFieldName = `units.${unitIndex}.heatLossDetails.floorElements`;

  const [defaultExternalAirTemperatureField] = useField(getUnitDefaultExternalAirTemperature(unitIndex));
  const { unitFloorElements, setUnitFloorElements } = useContext(DesignFormDefaultsContext);

  const floorElementFieldName = (floorElementIndex: number, fieldName: string) =>
    `${floorElementsFieldName}.${floorElementIndex}.${fieldName}`;

  const { heatLossDetails } = unit;

  const { externalAirTemperatureInDegreesCelsius: projectExternalAirTemperature } = assertNotNull(
    useContext(QuoteOrDesignFormContext),
  );

  const externalAirTemperatureInDegreesCelsius = isNaN(Number(defaultExternalAirTemperatureField.value))
    ? projectExternalAirTemperature
    : Number(defaultExternalAirTemperatureField.value);

  const newFloorElementNumber = useRef(heatLossDetails == null ? 1 : heatLossDetails.floorElements.length + 1);

  const addFloorElement = (fieldArrayRenderProps: FieldArrayRenderProps) => {
    const newFloorElement = {
      ...getDefaultUnitFloorElementFormModel(),
      name: `Floor Element ${newFloorElementNumber.current}`,
    };

    newFloorElementNumber.current++;

    fieldArrayRenderProps.push(newFloorElement);

    const newFloorManifoldTemperatures: Array<Array<{ temperature: string; downlossTypeCode: DownlossTypeCode }>> = [];
    unit.manifolds.forEach((manifold, manifoldIndex) => {
      const rooms = manifold.rooms || [];
      const newFloorRoomDetails: Array<{ temperature: string; downlossTypeCode: DownlossTypeCode }> = [];

      rooms.forEach((room, roomIndex) => {
        const roomHeatLossDetails = assertNotNull(room.heatLossDetails);

        const { floors } = roomHeatLossDetails;
        const newRoomFloorDetailsForm = getNewRoomFloorHeatLossDetailsFormModel(
          room.floorLevelCode,
          externalAirTemperatureInDegreesCelsius,
          unit,
        );

        fieldArrayRenderProps.form.setFieldValue(
          `units.${unitIndex}.manifolds.${manifoldIndex}.rooms.${roomIndex}.heatLossDetails.floors`,
          [...floors, newRoomFloorDetailsForm],
        );

        newFloorRoomDetails.push({
          temperature: newRoomFloorDetailsForm.temperatureBelowInDegreesCelsius,
          downlossTypeCode: newRoomFloorDetailsForm.downlossTypeCode,
        });
      });

      newFloorManifoldTemperatures.push(newFloorRoomDetails);
    });

    const newUnitFloorElement = {
      temperature: newFloorElement.defaultTemperatureBelowInDegreesCelsius,
      downlossTypeCode: newFloorElement.downlossTypeCode,
      manifoldRoomValues: newFloorManifoldTemperatures,
    };

    setUnitFloorElements([...unitFloorElements, newUnitFloorElement]);
  };

  const removeFloorElement = (floorElementIndex: number, fieldArrayRenderProps: FieldArrayRenderProps) => {
    fieldArrayRenderProps.remove(floorElementIndex);

    unit.manifolds.forEach((manifold, manifoldIndex) => {
      const rooms = manifold.rooms || [];

      rooms.forEach((room, roomIndex) => {
        const roomHeatLossDetails = assertNotNull(room.heatLossDetails);

        const { floors } = roomHeatLossDetails;

        fieldArrayRenderProps.form.setFieldValue(
          `units.${unitIndex}.manifolds.${manifoldIndex}.rooms.${roomIndex}.heatLossDetails.floors`,
          floors.filter((_, floorIndex) => floorIndex !== floorElementIndex),
        );
      });
    });

    setUnitFloorElements(unitFloorElements.filter((_, index) => index !== floorElementIndex));
  };

  const onDefaultDownlossTypeChange = (
    newDownlossType: DownlossTypeCode | null,
    floorElementIndex: number,
    fieldArrayRenderProps: FieldArrayRenderProps,
  ) => {
    if (newDownlossType == null) {
      return;
    }

    const downlossTypeDefaultTemperatures = getDownlossTypeDefaultTemperatures(externalAirTemperatureInDegreesCelsius);

    fieldArrayRenderProps.form.setFieldValue(
      floorElementFieldName(floorElementIndex, 'defaultTemperatureBelowInDegreesCelsius'),
      downlossTypeDefaultTemperatures[newDownlossType],
    );

    onUnitFloorTemperatureChange(
      downlossTypeDefaultTemperatures[newDownlossType],
      unitIndex,
      floorElementIndex,
      fieldArrayRenderProps.form,
      unitFloorElements,
      setUnitFloorElements,
      newDownlossType,
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedTemperatureChange = useCallback(
    debounce((newValue: string, floorIndex: number, fieldArrayRenderProps: FieldArrayRenderProps) => {
      onUnitFloorTemperatureChange(
        newValue,
        unitIndex,
        floorIndex,
        fieldArrayRenderProps.form,
        unitFloorElements,
        setUnitFloorElements,
      );
    }, 300),
    [unitFloorElements, setUnitFloorElements, unitIndex],
  );

  const handleTemperatureChange = (
    newValue: string,
    floorIndex: number,
    fieldArrayRenderProps: FieldArrayRenderProps,
  ) => {
    debouncedTemperatureChange(newValue, floorIndex, fieldArrayRenderProps);
  };

  if (heatLossDetails == null) {
    return null;
  }

  return (
    <FieldArray name={`units.${unitIndex}.heatLossDetails.floorElements`}>
      {fieldArrayRenderProps => (
        <Container>
          <Header3>Floor Elements</Header3>
          <Table>
            <THead>
              <Tr>
                <Th>Name</Th>
                <Th>Floor U-Value</Th>
                <Th>Default Downloss Type</Th>
                <Th>Default Temperature Below (°C)</Th>
                <DeleteButtonTableHeader />
              </Tr>
            </THead>
            <TBody>
              {heatLossDetails.floorElements.map((floorElement, index) => (
                <Tr key={index}>
                  <Td>
                    <TextInputField name={floorElementFieldName(index, 'name')} label={null} />
                  </Td>
                  <Td>
                    <TextInputField name={floorElementFieldName(index, 'floorThermalTransmittance')} label={null} />
                  </Td>
                  <Td>
                    <SelectField<DownlossTypeCode | null>
                      name={floorElementFieldName(index, 'downlossTypeCode')}
                      label={null}
                      options={downlossTypeCodeSelectOptions}
                      onChange={newType => onDefaultDownlossTypeChange(newType, index, fieldArrayRenderProps)}
                    />
                  </Td>
                  <Td>
                    <TextInputField
                      name={floorElementFieldName(index, 'defaultTemperatureBelowInDegreesCelsius')}
                      label={null}
                      onChange={newValue => handleTemperatureChange(newValue, index, fieldArrayRenderProps)}
                    />
                  </Td>
                  <Td>
                    {heatLossDetails.floorElements.length > 1 && (
                      <SecondaryButton
                        onClick={() => removeFloorElement(index, fieldArrayRenderProps)}
                        data-testid={deleteFloorElementButtonTestId(index)}
                      >
                        <ButtonIcon>
                          <DeleteIcon />
                        </ButtonIcon>
                      </SecondaryButton>
                    )}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
          <AddFloorElementButton onClick={() => addFloorElement(fieldArrayRenderProps)}>
            + Add Floor Element
          </AddFloorElementButton>
        </Container>
      )}
    </FieldArray>
  );
};

export const deleteFloorElementButtonTestId = (floorElementIndex: number) =>
  `delete-floor-element-button-${floorElementIndex}`;

const Container = styled.div`
  margin-bottom: ${spacing06};
`;

const DeleteButtonTableHeader = styled(Th)`
  width: 65px;
`;

const AddFloorElementButton = styled(SecondaryButton)`
  margin-top: ${spacing04};
`;
